// Angular
import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
// RxJS
import { Observable } from 'rxjs';
// CRUD
import { HttpUtilsService, QueryParamsModel, QueryResultsModel } from '../../_base/crud';
// Models
import { PatientHeightModel } from '../_models/patient-height.model';

// Ngrx
import { select, Store } from '@ngrx/store';
import { AppState } from '../../reducers';
import { currentAuthToken, currentUser } from '../../auth/_selectors/auth.selectors';
import { environment } from '../../../../environments/environment';

@Injectable()
export class PatientHeightsService {
  private authToken;
  private companyId;

  constructor(
    private http: HttpClient,
    private httpUtils: HttpUtilsService,
    private store: Store<AppState>,
  ) {
    this.store.pipe(select(currentAuthToken)).subscribe((res) => (this.authToken = res));
    this.store.pipe(select(currentUser)).subscribe((res) => (this.companyId = res.companyID));
  }

  // CREATE =>  POST: add a new patientHeight to the server
  createPatientHeight(
    patientHeight: PatientHeightModel,
    id: number,
  ): Observable<PatientHeightModel> {
    // Note: Add headers if needed (tokens/bearer)
    const url = environment.CORE_BASE + `/companies/${this.companyId}/patients/${id}/heights`;
    const body = patientHeight;
    const httpHeaders = this.httpUtils.getHTTPHeaders(this.authToken);
    return this.http.post<PatientHeightModel>(url, body, { headers: httpHeaders });
  }

  // READ
  getAllPatientHeights(): Observable<PatientHeightModel[]> {
    const url = environment.CORE_BASE + '/all-patient';
    const httpHeaders = this.httpUtils.getHTTPHeaders(this.authToken);
    return this.http.get<PatientHeightModel[]>(url, { headers: httpHeaders });
  }

  getPatientHeightById(patientHeightId: number): Observable<PatientHeightModel> {
    const body = {
      id: patientHeightId,
    };
    const url = environment.CORE_BASE + '/companies/{company_id}/patients?page=0&size=10';
    const httpHeaders = this.httpUtils.getHTTPHeaders(this.authToken);
    return this.http.post<PatientHeightModel>(url, body, { headers: httpHeaders });
  }

  // Method from server should return QueryResultsModel(items: any[], totalsCount: number)
  // items => filtered/sorted result
  // Server should return filtered/sorted result
  findPatientHeights(queryParams: QueryParamsModel, id: number): Observable<QueryResultsModel> {
    const httpHeaders = this.httpUtils.getHTTPHeaders(this.authToken);
    const url = environment.CORE_BASE + `/companies/${this.companyId}/patients/${id}/heights`;
    return this.http.get<QueryResultsModel>(url, { headers: httpHeaders });
  }

  // READ
  getPatientHeights(id): Observable<any> {
    const httpHeaders = this.httpUtils.getHTTPHeaders(this.authToken);
    const url = environment.CORE_BASE + `/companies/${this.companyId}/patients/${id}/heights`;
    return this.http.get<any>(url, { headers: httpHeaders });
  }

  // UPDATE => PUT: update the patient on the server
  updatePatientHeight(patientHeight: PatientHeightModel): Observable<any> {
    const httpHeader = this.httpUtils.getHTTPHeaders(this.authToken);
    const url = environment.CORE_BASE + `/companies/${this.companyId}/patients/${patientHeight.id}`;
    const body = patientHeight;
    return this.http.put(url, body, { headers: httpHeader });
  }

  // UPDATE Status
  updateStatusForPatientHeight(id: number, isActive: boolean): Observable<any> {
    const httpHeaders = this.httpUtils.getHTTPHeaders(this.authToken);
    const body = {
      id,
      isActive,
    };
    const url = environment.CORE_BASE + '/patientstatusupdatesingle';
    return this.http.put(url, body, { headers: httpHeaders });
  }

  // UPDATE Status
  updateStatusForPatientHeights(ids: number[], isActive: boolean): Observable<any> {
    const httpHeaders = this.httpUtils.getHTTPHeaders(this.authToken);
    const body = {
      ids,
      isActive,
    };
    const url = environment.CORE_BASE + '/patientstatusupdate';
    return this.http.put(url, body, { headers: httpHeaders });
  }

  // DELETE => delete the patient from the server
  deletePatientHeight(patientHeightId: number): Observable<any> {
    const url = environment.CORE_BASE + `/companies/${this.companyId}/patients/${patientHeightId}`;
    const httpHeaders = this.httpUtils.getHTTPHeaders(this.authToken);
    return this.http.delete<any>(url, { headers: httpHeaders });
  }

  deletePatientHeights(patientHeightsIds: number[] = []): Observable<any> {
    const url = environment.CORE_BASE + '/multiple-soft-delete-patient';
    const httpHeaders = this.httpUtils.getHTTPHeaders(this.authToken);
    const body = {
      ids: patientHeightsIds,
    };
    return this.http.post<any>(url, body, { headers: httpHeaders });
  }
}
